import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  MonitorSmartphone, BatteryCharging, PlugZap, Layers, Camera, Droplets,
  RefreshCcw, HardDrive, Laptop, Search, ChevronDown, ArrowRight, ShieldCheck,
  Smartphone, Wrench, Sparkles,
} from "lucide-react";
import { SERVICES } from "../data/services";
import { Reveal } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";
import { PrimaryCTA, GhostCTA } from "../components/CTA";
import { FloatingIcons } from "../components/FloatingIcons";
import { DeviceDrift } from "../components/DeviceDrift";
import { RepairStage } from "../components/RepairStage";
import { WaterRescue } from "../components/WaterRescue";
import { FixBothScroll } from "../components/FixBothScroll";
import { TechVideo } from "../components/TechVideo";
import { YTVideoPanel } from "../components/YTVideoPanel";
import { SlideIn } from "../components/Motion";
import { LiminalSection, HudFrame } from "../components/Liminal";
import { TrainRunner } from "../components/TrainRunner";
import { playBlip } from "../utils/sound";
import { SITE } from "../data/site";
import { useLang } from "../i18n";

const ICONS = {
  crack: MonitorSmartphone, battery: BatteryCharging, plug: PlugZap, layers: Layers,
  camera: Camera, droplets: Droplets, refresh: RefreshCcw, harddrive: HardDrive,
  laptop: Laptop, search: Search, sparkles: Sparkles,
};

const ISSUE_LINK = {
  screen: "screen", battery: "battery", charging: "charging", backglass: "backglass",
  camera: "camera", water: "water", software: "software", data: "data",
};

const ServiceRow = ({ service, open, onToggle, onHover }) => {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const Icon = ICONS[service.icon] || Search;
  const issueParam = ISSUE_LINK[service.id];
  const text = t(`lab.services.${service.id}`);
  return (
    <Reveal>
      <div
        data-testid={`service-item-${service.id}`}
        className={`overflow-hidden rounded-2xl border transition-colors duration-500 ${
          open
            ? "border-ink/20 bg-white/[0.06] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.8)]"
            : "border-sand bg-white/[0.04] hover:bg-white/[0.08]"
        }`}
      >
        <button
          type="button"
          data-testid={`service-toggle-${service.id}`}
          aria-expanded={open}
          onClick={onToggle}
          onMouseEnter={onHover}
          onFocus={onHover}
          className="flex w-full items-center gap-4 sm:gap-6 px-5 sm:px-8 py-5 sm:py-6 text-start"
        >
          <span className="font-mono text-xs sm:text-sm text-gold">{service.num}</span>
          <span
            className={`flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
              open ? "border-accent bg-accent text-[#EDF1F7]" : "border-ink/15 text-ink"
            }`}
          >
            <Icon className="h-5 w-5" />
          </span>
          <span className="flex-1 font-display text-lg sm:text-2xl font-medium tracking-tight">
            {text.title}
          </span>
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="text-ink/50"
          >
            <ChevronDown className="h-5 w-5" />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="body"
              initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
              animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
              exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="grid grid-cols-1 gap-6 px-5 sm:px-8 pb-7 sm:grid-cols-12">
                <p className="text-sm sm:text-base leading-relaxed text-ink/70 sm:col-span-7 sm:ps-[4.5rem]">
                  {text.blurb}
                </p>
                <div className="sm:col-span-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/50">
                    {t("lab.notice")}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {text.symptoms.map((s) => (
                      <span key={s} className="rounded-full bg-cream px-3 py-1.5 text-xs text-ink/75">
                        {s}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={issueParam ? `/quote?issue=${issueParam}` : "/quote"}
                    data-testid={`service-quote-link-${service.id}`}
                    className="group mt-5 inline-flex items-center gap-2 font-semibold text-accent-bright transition-colors hover:text-accent-soft"
                  >
                    {t("lab.ask")}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

export default function RepairLab() {
  const { t } = useLang();
  const [openId, setOpenId] = useState("screen");
  const [stageId, setStageId] = useState("screen");
  return (
    <>
      <section data-testid="repair-lab-hero" className="grain relative overflow-hidden pt-28 sm:pt-36 lg:pt-44 pb-10 sm:pb-14">
        <div className="pointer-events-none absolute -top-24 -left-32 h-[26rem] w-[26rem] rounded-full bg-accent/15 blur-3xl" aria-hidden="true" />
        <FloatingIcons
          items={[
            { Icon: Smartphone, x: "88%", y: "20%", dur: 10, opacity: "text-cyan/20" },
            { Icon: Wrench, x: "6%", y: "58%", dur: 12, delay: 0.7, opacity: "text-volt/20" },
          ]}
        />
        <DeviceDrift device="cracked" className="right-[2%] top-6 lg:right-[3%] lg:top-10" imgClassName="h-[150px] sm:h-[220px] lg:h-[360px]" dur={11} opacity="opacity-40 lg:opacity-70" />
        <DeviceDrift device="pluto" className="left-[4%] bottom-[8%]" imgClassName="h-[42px] lg:h-[80px]" dur={15} delay={0.6} opacity="opacity-50 lg:opacity-70" />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <SectionHead
            chapter={t("lab.chapter")}
            eyebrow={t("lab.eyebrow")}
            title={<>{t("lab.titleA")} <span className="italic text-accent-bright">{t("lab.titleEm")}</span></>}
            lede={t("lab.lede")}
          />
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-4 pb-24 sm:px-8 sm:pb-32 lg:px-16">
        <FloatingIcons
          items={[
            { Icon: Droplets, x: "96%", y: "30%", dur: 11 },
            { Icon: BatteryCharging, x: "1%", y: "74%", dur: 9, delay: 1.2 },
          ]}
        />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7">
            {SERVICES.map((s) => (
              <ServiceRow
                key={s.id}
                service={s}
                open={openId === s.id}
                onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                onHover={() => setStageId(s.id)}
              />
            ))}
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28">
              <Reveal>
                <HudFrame label="Diagnostic Bay" coords="3012 17 Ave SE · 51.04N" className="p-1.5">
                  <RepairStage
                    activeId={stageId || openId}
                    services={SERVICES}
                    onPick={(id) => { playBlip(520); setStageId(id); setOpenId(id); }}
                  />
                </HudFrame>
              </Reveal>
            </div>
          </div>
        </div>

        <Reveal className="mt-14">
          <div
            data-testid="pricing-disclaimer"
            className="flex flex-col gap-4 rounded-2xl border border-gold/30 bg-gold/10 px-6 py-6 sm:flex-row sm:items-start sm:gap-5 sm:px-8"
          >
            <ShieldCheck className="h-6 w-6 shrink-0 text-gold" />
            <div>
              <p className="font-display text-xl font-medium tracking-tight">{t("lab.pricingTitle")}</p>
              <p className="mt-2 text-sm sm:text-base leading-relaxed text-ink/70">
                {t("lab.pricingBody")}
              </p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="lab-price-chips">
                {t("teaser.prices").map((p) => (
                  <span
                    key={p}
                    className="rounded-full border border-gold/35 bg-gold/[0.08] px-3.5 py-1.5 font-mono text-xs text-gold"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <WaterRescue />

      <FixBothScroll />

      <LiminalSection env="station" testid="lab-video-section" className="border-y border-sand/60">
        <TrainRunner />
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-8 sm:py-24">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <SlideIn from="left">
              <YTVideoPanel videoId="RU0xGb5yW2c" label="Ambient tech clip three" testid="repair-lab-video" className="aspect-[4/5] sm:aspect-[4/3]" />
            </SlideIn>
            <SlideIn from="right" delay={0.1}>
              <YTVideoPanel videoId="nHJthK_2-_Q" label="Ambient tech clip four" testid="repair-lab-video-2" className="aspect-[4/5] sm:aspect-[4/3]" />
            </SlideIn>
          </div>
        </div>
      </LiminalSection>
      <section className="grain border-y border-ink/10 bg-espresso">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 py-16 sm:px-8 sm:py-20 lg:flex-row lg:items-center lg:justify-between lg:px-16">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">
              {t("lab.ctaTitleA")} <span className="italic text-gold-soft">{t("lab.ctaTitleEm")}</span>
            </h2>
            <p className="mt-3 max-w-lg text-ink/65">
              {t("lab.ctaBody")}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <PrimaryCTA to="/quote" testid="repairlab-quote-cta" dark>{t("lab.ctaQuote")}</PrimaryCTA>
            <GhostCTA href={SITE.phoneHref} testid="repairlab-call-cta" dark>{t("lab.ctaCall")} {SITE.phoneDisplay}</GhostCTA>
          </div>
        </div>
      </section>
    </>
  );
}
